import React, { useState, useEffect, useMemo } from 'react';
import { Accordion } from 'react-bootstrap';
import { HiCurrencyDollar, HiCube, HiTrophy, HiChevronDown, HiTag } from 'react-icons/hi2';

import { GetStatsForCurrentSession, GetStatsByRange } from '../../../../../../crud/menu_api/item_stats';
import './ProductStats.scss';
import { generateProductStatsFakeData } from './fakeDataGenerator';

// --- TYPE DEFINITIONS ---
interface ItemStat {
    item_name: string;
    category_id: number;
    category_name: string;
    total_quantity_sold: number;
    total_monetary_value: number;
    total_percentage: number;
    // NEW: Granel (weight/volume) support fields
    total_granel_amount?: number;
    granel_orders_count?: number;
    unidad_medida_id?: number | null;
    unidad_medida_breakdown?: {
        [unidadMedidaId: string]: {
            granel_amount: number;
            orders_count: number;
        };
    };
    // Enhanced breakdown with granel support
    by_pedido_type?: {
        [pedidoTypeId: string]: {
            name: string;
            quantity: number;
            monetary_value: number;
            granel_amount?: number;
            granel_orders_count?: number;
        };
    };
    by_user?: {
        [userId: string]: {
            username: string;
            quantity: number;
            monetary_value: number;
            granel_amount?: number;
            granel_orders_count?: number;
        };
    };
    by_third_party?: {
        [tppId: string]: {
            provider_name: string;
            quantity: number;
            monetary_value: number;
            granel_amount?: number;
            granel_orders_count?: number;
        };
    };
    accompanied_with: {
        selection_groups: { [groupId: string]: { [itemId: string]: { item_name: string; count: number } } };
        modifiers: { [modifierId: string]: { [optionId: string]: { option_name: string; count: number } } };
    };
}

interface GenericStat {
    name?: string;
    username?: string;
    category_name?: string;
    provider_name?: string;
    total_quantity_sold: number;
    total_monetary_value: number;
    total_percentage: number;
    // NEW: Granel support for generic stats
    total_granel_amount?: number;
    granel_orders_count?: number;
}

type AggregationType = 'by_item' | 'by_user' | 'by_pedido_type' | 'by_menu_category';
type TimeRange = 'current' | '7d' | '30d' | 'custom';

// --- HELPER FUNCTIONS ---
const formatCurrency = (value: number) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(value);
const toTitleCase = (str: string) => {
    if (!str) return '';
    return str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());
};

// NEW: Unit mapping for granel products
const getUnitLabel = (unidadMedidaId: number | null | undefined): string => {
    if (!unidadMedidaId) return '';
    
    const unitMapping: { [key: number]: string } = {
        1: 'kg',     // Kilogramos
        2: 'gr',     // Gramos
        3: 'L',      // Litros
        4: 'mL',     // Mililitros
        5: 'u',      // Unidad
        6: 'pk',     // Pack
        7: 'cj',     // Caja
        8: 'dcn',    // Docena
        9: 'cm',     // Centímetro
        10: 'm',     // Metro
        11: 'm²',    // Bandeja
        12: 'lb',    // Libra
        13: 'lb',    // Libra (duplicate in your table)
        14: 'u',     // Onza líquida (US) - using 'u' as fallback
        15: 'fl oz', // Onza líquida (US)
    };
    
    return unitMapping[unidadMedidaId] || 'u';
};

// NEW: Format granel amount with proper units
const formatGranelAmount = (amount: number, unidadMedidaId?: number | null): string => {
    if (amount === 0) return '';
    
    const unit = getUnitLabel(unidadMedidaId);
    const formattedAmount = amount % 1 === 0 ? amount.toString() : amount.toFixed(2);
    
    return `${formattedAmount} ${unit}`;
};

// NEW: Check if item is primarily granel (has granel amounts but no regular quantities)
const isGranelItem = (item: ItemStat): boolean => {
    return (item.total_granel_amount || 0) > 0 && (item.total_quantity_sold || 0) === 0;
};

// NEW: Check if item is mixed (has both granel and regular quantities)
const isMixedItem = (item: ItemStat): boolean => {
    return (item.total_granel_amount || 0) > 0 && (item.total_quantity_sold || 0) > 0;
};

// --- SUB-COMPONENTS ---

// NEW: Component to display granel unit breakdown
const GranelBreakdown = ({ item }: { item: ItemStat }) => {
    const breakdown = item.unidad_medida_breakdown;
    
    if (!breakdown || Object.keys(breakdown).length <= 1) {
        return null;
    }

    return (
        <div className="granel-breakdown" style={{ marginTop: '0.75rem', padding: '0.75rem', borderRadius: '0.75rem', background: 'var(--card-color)', border: '1px solid var(--border-color)' }}>
            <h6 style={{ fontSize: '0.75rem', fontWeight: 600, letterSpacing: '0.03em', textTransform: 'uppercase', color: 'var(--text-color)', opacity: 0.6, marginBottom: '0.5rem' }}>Desglose por unidad de medida</h6>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.375rem' }}>
                {Object.entries(breakdown).map(([unitId, data]) => (
                    <div key={unitId} className="flex items-center gap-3 py-1.5 px-2 rounded-lg" style={{ background: 'var(--background-color)' }}>
                        <span className="mp-badge-amber" style={{ minWidth: '4.5rem', textAlign: 'center' }}>{formatGranelAmount(data.granel_amount, parseInt(unitId))}</span>
                        <span style={{ fontSize: '0.8125rem', color: 'var(--text-color)', opacity: 0.5 }}>({data.orders_count} órdenes)</span>
                        <span style={{ marginLeft: 'auto', fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-color)' }}>{formatCurrency((data.granel_amount / (item.total_granel_amount || 1)) * item.total_monetary_value)}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

const AccompanimentLeaderboard = ({ item }: { item: ItemStat }) => {
    const sortedCompanions = useMemo(() => {
        const allCompanions: { name: string, count: number }[] = [];
        if (item.accompanied_with) {
            Object.values(item.accompanied_with.selection_groups || {}).forEach(group => 
                Object.values(group).forEach(selItem => allCompanions.push({ name: selItem.item_name, count: selItem.count }))
            );
            Object.values(item.accompanied_with.modifiers || {}).forEach(mod => 
                Object.values(mod).forEach(opt => allCompanions.push({ name: opt.option_name, count: opt.count }))
            );
        }
        return allCompanions.sort((a, b) => b.count - a.count);
    }, [item]);

    const total = useMemo(() => sortedCompanions.reduce((s, c) => s + c.count, 0), [sortedCompanions]);
    const [showAll, setShowAll] = useState(false);

    if (sortedCompanions.length === 0) {
        return null;
    }

    const list = showAll ? sortedCompanions : sortedCompanions.slice(0, 5);

    return (
        <div style={{ marginTop: '0.75rem', paddingTop: '0.75rem', borderTop: '1px solid var(--border-color)' }}>
            <div style={{ fontSize: '0.625rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-color)', opacity: 0.4, marginBottom: '0.625rem' }}>Acompañamientos más frecuentes</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                {list.map((comp, index) => {
                    const pct = total > 0 ? (comp.count / total) * 100 : 0;
                    return (
                        <div key={index} style={{
                            display: 'grid',
                            gridTemplateColumns: '1.5rem 1fr auto',
                            alignItems: 'center',
                            gap: '0.625rem',
                            padding: '0.375rem 0.625rem',
                            borderRadius: '0.5rem',
                            background: index % 2 === 0 ? 'var(--background-color)' : 'transparent',
                        }}>
                            <span style={{
                                fontSize: '0.6875rem',
                                fontWeight: 700,
                                textAlign: 'center',
                                color: index < 3 ? '#FC7556' : 'var(--text-color)',
                                opacity: index < 3 ? 1 : 0.35,
                            }}>
                                {index + 1}
                            </span>
                            <span style={{ fontSize: '0.8125rem', fontWeight: 500, color: 'var(--text-color)' }}>{toTitleCase(comp.name)}</span>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                <span style={{
                                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                    minWidth: '1.75rem', padding: '0.125rem 0.375rem',
                                    borderRadius: '0.375rem', fontSize: '0.6875rem', fontWeight: 700,
                                    background: 'rgba(252, 117, 86, 0.1)', color: '#FC7556',
                                }}>{comp.count}x</span>
                                <span style={{ fontSize: '0.625rem', fontWeight: 600, color: 'var(--text-color)', opacity: 0.3, minWidth: '2rem', textAlign: 'right' }}>{Math.round(pct)}%</span>
                            </span>
                        </div>
                    );
                })}
            </div>
            {sortedCompanions.length > 5 && (
                <button
                    onClick={() => setShowAll(v => !v)}
                    style={{
                        marginTop: '0.5rem', padding: '0.25rem 0.75rem',
                        fontSize: '0.6875rem', fontWeight: 600,
                        color: '#FC7556', background: 'transparent',
                        border: '1px solid rgba(252, 117, 86, 0.2)',
                        borderRadius: '0.5rem', cursor: 'pointer',
                    }}
                >
                    {showAll ? 'Ver menos' : `Ver más (${sortedCompanions.length - 5})`}
                </button>
            )}
        </div>
    );
};


// --- MAIN DASHBOARD COMPONENT ---
interface ProductStatsProps {
  timeRange?: TimeRange;
  aggregationType?: AggregationType;
  onTimeRangeChange?: (timeRange: TimeRange) => void;
  onAggregationTypeChange?: (aggregationType: AggregationType) => void;
  startDate?: string;
  endDate?: string;
}

const ProductStats: React.FC<ProductStatsProps> = ({
  timeRange = 'current',
  aggregationType = 'by_item',
  startDate,
  endDate
}) => {
    const [loading, setLoading] = useState<boolean>(true);
    const [statsData, setStatsData] = useState<any>({});
    const [localId, setLocalId] = useState<number | null>(null);
    const [isMobile, setIsMobile] = useState(false);


    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 900);
        };

        window.addEventListener('resize', handleResize);
        handleResize(); // Call it initially to set the correct value

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, []);

    useEffect(() => {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        setLocalId(user?.local_id || null);
    }, []);

    useEffect(() => {
        const demoMode = localStorage.getItem('demoMode') === 'true';

        if (demoMode) {
            setLoading(true);
            const fake = generateProductStatsFakeData(aggregationType, timeRange);
            setStatsData(fake);
            setLoading(false);
            return;
        }

        if (localId === null) return;
        setLoading(true);
        const requestBody = { local_id: localId, aggregation_type: aggregationType };

        let promise;
        if (timeRange === 'current') {
            promise = GetStatsForCurrentSession(requestBody);
        } else if (timeRange === 'custom' && startDate && endDate) {
            // Use custom date range from calendar pickers
            promise = GetStatsByRange({ 
                ...requestBody, 
                start_date: startDate, 
                end_date: endDate
            });
        } else {
            // Use predefined ranges (7d, 30d)
            const endDateCalc = new Date();
            const startDateCalc = new Date();
            startDateCalc.setDate(endDateCalc.getDate() - (timeRange === '7d' ? 7 : 30));
            
            promise = GetStatsByRange({ 
                ...requestBody, 
                start_date: startDateCalc.toISOString().slice(0, 10), 
                end_date: endDateCalc.toISOString().slice(0, 10)
            });
        }

        promise.then(response => {
            setStatsData((response as any).data || {});
        }).catch(error => {
            console.error(`Error fetching stats for ${timeRange} with type ${aggregationType}:`, error);
            setStatsData({});
        }).finally(() => {
            setLoading(false);
        });

    }, [localId, aggregationType, timeRange, startDate, endDate]);

    const sortedData = useMemo(() => {
        if (!statsData || typeof statsData !== 'object') return [];
        return Object.entries(statsData).sort(([, a], [, b]) => (b as any).total_monetary_value - (a as any).total_monetary_value);
    }, [statsData]);

    const totalRevenue = useMemo(() => sortedData.reduce((sum, [, item]) => sum + (item as any).total_monetary_value, 0), [sortedData]);
    const totalQuantity = useMemo(() => sortedData.reduce((sum, [, item]) => sum + (item as any).total_quantity_sold, 0), [sortedData]);
    
    // NEW: Calculate granel totals
    const totalGranelOrders = useMemo(() => sortedData.reduce((sum, [, item]) => sum + ((item as any).granel_orders_count || 0), 0), [sortedData]);
    const hasGranelItems = useMemo(() => sortedData.some(([, item]) => ((item as any).total_granel_amount || 0) > 0), [sortedData]);

    const renderStatCards = () => {
        const cards = [
            { icon: HiCurrencyDollar, iconClass: 'mp-stat-card-icon-green', label: 'Ingresos', value: formatCurrency(totalRevenue || 0) },
            { icon: HiCube, iconClass: 'mp-stat-card-icon-blue', label: hasGranelItems ? 'Unidades + Granel' : 'Unidades', value: `${totalQuantity || 0}${hasGranelItems && totalGranelOrders > 0 ? ` + ${totalGranelOrders} a granel` : ''}` },
            { icon: HiTrophy, iconClass: 'mp-stat-card-icon-amber', label: 'Más vendido', value: sortedData.length > 0 ? toTitleCase((sortedData[0][1] as any).item_name || (sortedData[0][1] as any).username || (sortedData[0][1] as any).category_name || (sortedData[0][1] as any).name) : 'Ninguno' },
        ];
        return (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
                {cards.map((c, i) => (
                    <div key={i} className="mp-stat-card">
                        <div className={`mp-stat-card-icon ${c.iconClass}`}><c.icon /></div>
                        <div className="mp-stat-card-label">{c.label}</div>
                        <div className="mp-stat-card-value" style={{ fontSize: c.label === 'Más vendido' ? '0.95rem' : undefined }}>{c.value}</div>
                    </div>
                ))}
            </div>
        );
    };
    
    const renderItemDetailsList = () => (
        <div className="mp-table-card details-list">
            <div className="product-list-header">
                <div className="col-item">Producto</div>
                <div className="col-qty">Cantidad</div>
                <div className="col-value">Valor total</div>
                {!isMobile && <div className="col-percent">Contribución</div>}
            </div>
            {sortedData.map(([id, itemData], idx) => {
                const item = itemData as ItemStat;
                const hasAccompaniments = Object.keys(item.accompanied_with?.selection_groups || {}).length > 0 || Object.keys(item.accompanied_with?.modifiers || {}).length > 0;
                const hasGranelBreakdown = item.unidad_medida_breakdown && Object.keys(item.unidad_medida_breakdown).length > 1;
                const hasExpandableContent = hasAccompaniments || hasGranelBreakdown;
                const rank = idx + 1;
                return (
                    <Accordion key={id} className={`item-accordion rank-${rank}`}>
                        <Accordion.Item eventKey={id.toString()}>
                            <Accordion.Header as="div" className={hasExpandableContent ? 'clickable' : ''}>
                                <div className="item-summary">
                                    <div className="col-item">
                                        <span className={`mp-rank-badge ${rank === 1 ? 'mp-rank-gold' : rank === 2 ? 'mp-rank-silver' : rank === 3 ? 'mp-rank-bronze' : ''}`}>
                                            {rank <= 3 ? <HiTrophy /> : rank}
                                        </span>
                                        <span className="item-name">{toTitleCase(item.item_name)}</span>
                                        <span className="category-chip">
                                          <HiTag style={{ fontSize: '0.7rem' }} /> {(!item.category_name || item.category_name === "Uncategorized") ? "Sin categoría" : toTitleCase(item.category_name)}
                                        </span>
                                    </div>
                                    <div className="col-qty">
                                        {isGranelItem(item) ? (
                                            <div className="flex flex-col items-center gap-0.5">
                                                <span className="mp-badge-amber">{formatGranelAmount(item.total_granel_amount || 0, item.unidad_medida_id)}</span>
                                                <span style={{ fontSize: '0.6rem', color: 'var(--text-color)', opacity: 0.35 }}>({item.granel_orders_count || 0} órd.)</span>
                                            </div>
                                        ) : isMixedItem(item) ? (
                                            <div className="flex flex-col items-center gap-0.5">
                                                <span>{item.total_quantity_sold || 0}u</span>
                                                <span className="mp-badge-amber" style={{ fontSize: '0.6rem' }}>+ {formatGranelAmount(item.total_granel_amount || 0, item.unidad_medida_id)}</span>
                                            </div>
                                        ) : (
                                            <span>{item.total_quantity_sold || 0}</span>
                                        )}
                                    </div>
                                    <div className="col-value">
                                        {formatCurrency(item.total_monetary_value || 0)}
                                        {isMobile && <span className="mp-badge-neutral" style={{ fontSize: '0.6rem', marginLeft: '0.25rem' }}>{`${Math.round(item.total_percentage || 0)}%`}</span>}
                                    </div>
                                    {!isMobile && (
                                        <div className="col-percent">
                                            <div className="contribution-bar-wrap">
                                                <div className="contribution-bar" style={{ width: `${Math.min(item.total_percentage || 0, 100)}%` }} />
                                            </div>
                                            <span className="contribution-label">{Math.round(item.total_percentage || 0)}%</span>
                                        </div>
                                    )}
                                    {hasExpandableContent && <HiChevronDown className="accordion-chevron" />}
                                </div>
                            </Accordion.Header>
                            {hasExpandableContent && (
                                <Accordion.Body>
                                    <GranelBreakdown item={item} />
                                    <AccompanimentLeaderboard item={item} />
                                </Accordion.Body>
                            )}
                        </Accordion.Item>
                    </Accordion>
                );
            })}
        </div>
    );
    
    const renderGenericList = (titleColName: string, valueColName: keyof GenericStat) => (
         <div className="mp-table-card details-list">
            <div className="product-list-header">
                <div className="col-item">{titleColName}</div>
                <div className="col-qty">Cantidad</div>
                <div className="col-value">Valor Total</div>
                <div className="col-percent">Contribución</div>
            </div>
            {sortedData.map(([id, data], idx) => {
                const item = data as GenericStat;
                const hasGranel = (item.total_granel_amount || 0) > 0;
                const isOnlyGranel = hasGranel && (item.total_quantity_sold || 0) === 0;
                
                return (
                    <div key={id} className="generic-item-row">
                        <div className="col-item">
                            <span className={`mp-rank-badge ${idx === 0 ? 'mp-rank-gold' : idx === 1 ? 'mp-rank-silver' : idx === 2 ? 'mp-rank-bronze' : ''}`}>
                                {idx < 3 ? <HiTrophy /> : idx + 1}
                            </span>
                            <span className="item-name">{toTitleCase(String(item[valueColName]) || '')}</span>
                        </div>
                        <div className="col-qty">
                            {isOnlyGranel ? (
                                <span style={{ fontSize: '0.8125rem', color: 'var(--text-color)', opacity: 0.6 }}>{item.granel_orders_count || 0} a granel</span>
                            ) : hasGranel ? (
                                <span style={{ fontSize: '0.8125rem' }}>{item.total_quantity_sold}u + {item.granel_orders_count || 0} granel</span>
                            ) : (
                                <span style={{ fontWeight: 600 }}>{item.total_quantity_sold}</span>
                            )}
                        </div>
                        <div className="col-value">{formatCurrency(item.total_monetary_value)}</div>
                        <div className="col-percent">
                            <div className="contribution-bar-wrap">
                                <div className="contribution-bar" style={{ width: `${Math.min(item.total_percentage || 0, 100)}%` }} />
                            </div>
                            <span className="contribution-label">{Math.round(item.total_percentage)}%</span>
                        </div>
                    </div>
                );
            })}
        </div>
    );

    const renderContent = () => {
        if (loading) return (
            <div className="mp-empty-state">
                <div className="mp-empty-state-icon" style={{ color: 'var(--mediopunto-orange)' }}>
                    <svg className="animate-spin" style={{ width: '1.5rem', height: '1.5rem' }} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" style={{ opacity: 0.25 }}/><path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" style={{ opacity: 0.75 }}/></svg>
                </div>
                <p className="mp-empty-state-title">Cargando estadísticas...</p>
            </div>
        );
        if (sortedData.length === 0) return (
            <div className="mp-empty-state">
                <div className="mp-empty-state-icon"><HiCube /></div>
                <p className="mp-empty-state-title">No hay datos de ventas para el período seleccionado</p>
            </div>
        );
        
        switch(aggregationType) {
            case 'by_item': return renderItemDetailsList();
            case 'by_user': return renderGenericList("Usuario", "username");
            case 'by_menu_category': return renderGenericList("Categoría", "category_name");
            case 'by_pedido_type': return renderGenericList("Canal de Venta", "name");
            default: return <div>Vista no implementada</div>;
        }
    };

    return (
        <div className="product-stats-container flex flex-col gap-0">
            {renderStatCards()}
            {renderContent()}
        </div>
    );
};

export default ProductStats;
